/*
 * revQ icon set — DO NOT EDIT, DO NOT REDRAW.
 * Import these components wherever the side menu and header need an icon.
 * Paths are copied verbatim from @tabler/icons 3.19.0 (outline), except:
 *   IconHome — Tabler "home" with its door path removed
 *   IconLens — Tabler "focus-centered" with the centre dot enlarged to r=2.5 (the Lens mark)
 * Tabler Icons: MIT License, Copyright (c) 2020-2024 Paweł Kuna.
 *
 * Usage: <IconSettings />            side menu default (18px, stroke 1.75)
 *        <IconSearch size={16} />     header
 * Colour comes from the parent's CSS `color` (currentColor).
 */
import React from 'react';

const base = (paths, displayName) => {
  const C = ({ size = 18, stroke = 1.75, className, title, ...rest }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {paths.map((d) => <path key={d} d={d} />)}
    </svg>
  );
  C.displayName = displayName;
  return C;
};

// home (door path removed)
export const IconHome = base([
  "M5 12l-2 0l9 -9l9 9l-2 0",
  "M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7"
], 'IconHome');

// video
export const IconTelemedicine = base([
  "M15 10l4.553 -2.276a1 1 0 0 1 1.447 .894v6.764a1 1 0 0 1 -1.447 .894l-4.553 -2.276v-4z",
  "M3 6m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z"
], 'IconTelemedicine');

// clipboard-check
export const IconEncounterReview = base([
  "M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2",
  "M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z",
  "M9 14l2 2l4 -4"
], 'IconEncounterReview');

// microphone
export const IconDictation = base([
  "M9 2m0 3a3 3 0 0 1 3 -3h0a3 3 0 0 1 3 3v5a3 3 0 0 1 -3 3h0a3 3 0 0 1 -3 -3z",
  "M5 10a7 7 0 0 0 14 0",
  "M8 21l8 0",
  "M12 17l0 4"
], 'IconDictation');

// focus-centered (centre ring r=2.5) — Lens mark
export const IconLens = base([
  "M4 8v-2a2 2 0 0 1 2 -2h2",
  "M4 16v2a2 2 0 0 0 2 2h2",
  "M16 4h2a2 2 0 0 1 2 2v2",
  "M16 20h2a2 2 0 0 0 2 -2v-2",
  "M12 12m-2.5 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 1 0 -5 0"
], 'IconLens');

// files
export const IconClinicDocuments = base([
  "M15 3v4a1 1 0 0 0 1 1h4",
  "M18 17h-7a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h4l5 5v7a2 2 0 0 1 -2 2z",
  "M16 17v2a2 2 0 0 1 -2 2h-7a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h2"
], 'IconClinicDocuments');

// file-invoice
export const IconClaimsQueue = base([
  "M14 3v4a1 1 0 0 0 1 1h4",
  "M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z",
  "M9 7l1 0",
  "M9 13l6 0",
  "M13 17l2 0"
], 'IconClaimsQueue');

// receipt
export const IconClaims = base([
  "M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16l-3 -2l-2 2l-2 -2l-2 2l-2 -2l-3 2m4 -14h6m-6 4h6m-2 4h2"
], 'IconClaims');

// cash-banknote
export const IconPaymentPosting = base([
  "M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0",
  "M3 6m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z",
  "M18 12l.01 0",
  "M6 12l.01 0"
], 'IconPaymentPosting');

// text-size
export const IconTextSize = base([
  "M3 7v-2h13v2",
  "M10 5v14",
  "M12 19h-4",
  "M15 13v-1h6v1",
  "M18 12v7",
  "M17 19h2"
], 'IconTextSize');

// settings
export const IconSettings = base([
  "M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z",
  "M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"
], 'IconSettings');

// search
export const IconSearch = base([
  "M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0",
  "M21 21l-6 -6"
], 'IconSearch');

// bolt
export const IconCredits = base([
  "M13 3l0 7l6 0l-8 11l0 -7l-6 0l8 -11"
], 'IconCredits');

// logout
export const IconSignOut = base([
  "M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2",
  "M9 12h12l-3 -3",
  "M18 15l3 -3"
], 'IconSignOut');

// selector
export const IconSwitch = base([
  "M8 9l4 -4l4 4",
  "M16 15l-4 4l-4 -4"
], 'IconSwitch');


// stack-2
export const IconExperienceMode = base([
  "M12 4l-8 4l8 4l8 -4l-8 -4",
  "M4 12l8 4l8 -4",
  "M4 16l8 4l8 -4"
], 'IconExperienceMode');

/* Which icon goes where. Use this map instead of choosing icons by hand. */
export const MENU_ICONS = {
  home: IconHome,
  telemedicine: IconTelemedicine,
  encounterReview: IconEncounterReview,
  dictation: IconDictation,
  chartDocuments: IconLens,
  clinicDocuments: IconClinicDocuments,
  claimsQueue: IconClaimsQueue,
  claims: IconClaims,
  paymentPosting: IconPaymentPosting,
  experienceMode: IconExperienceMode,
  textSize: IconTextSize,
  settings: IconSettings,
};
export const HEADER_ICONS = {
  search: IconSearch,
  upload: IconLens,
  credits: IconCredits,
  signOut: IconSignOut,
  clientSwitch: IconSwitch,
};
